/**
 * Apartsales — /favorites (Tab «Избранные»).
 *
 * Структура (сверху вниз):
 *   1. FavoritesHeader      — счётчик + сводка (стоимость портфеля, средний yield)
 *   2. FavoritesTabs        — Объекты / Коллекции / Алерты / Просмотрено
 *   3. (если Объекты):
 *      • LocationFilter     — иерархический drill-down (страна→регион→город→район)
 *      • ViewControls       — сортировка + flat / grouped toggle
 *      • Лента UnitCard или сгруппированные секции
 *
 * Витринная карточка одна — большая (UnitCard). Средний и мелкий виды
 * добавятся отдельным проходом (см. memory).
 *
 * Sync с БД (отдельный шаг после approval UI):
 *   - `favorites` (user_id, unit_id) — уже есть в схеме
 *   - `favorite_collections` + junction — добавить миграцией
 *   - `projects.country` slug — добавить миграцией
 *   - API `GET /favorites/list?collection={id}` — отдельный endpoint
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { UnitCard } from '~/components/UnitCard';
import { FavoritesHeader } from '~/components/favorites/FavoritesHeader';
import { FavoritesTabs, type FavoritesTab } from '~/components/favorites/FavoritesTabs';
import {
  LocationFilter,
  type LocationSelection,
} from '~/components/favorites/LocationFilter';
import {
  ViewControls,
  type SortMode,
  type GroupMode,
} from '~/components/favorites/ViewControls';
import { CollectionsTab } from '~/components/favorites/CollectionsTab';
import { CollectionGlyph } from '~/components/favorites/collection-glyphs';
import { TabStub } from '~/components/TabStub';
import {
  FAVORITED_PROPERTIES,
  USER_COLLECTIONS,
} from '~/data/favorites-mock';
import type { Property } from '~/data/mock';

export const Route = createFileRoute('/favorites')({
  component: FavoritesScreen,
});

/* ────────────────────────────────────────────────────────────── */
/*  Filter + Sort helpers                                          */
/* ────────────────────────────────────────────────────────────── */

function applyLocationFilter(items: Property[], sel: LocationSelection): Property[] {
  return items.filter((p) => {
    const loc = p.locationDetails;
    if (!loc) return false;
    if (sel.countrySlug && loc.countrySlug !== sel.countrySlug) return false;
    if (sel.regionSlug && loc.regionSlug !== sel.regionSlug) return false;
    if (sel.citySlug && loc.citySlug !== sel.citySlug) return false;
    if (sel.districtSlug && loc.districtSlug !== sel.districtSlug) return false;
    return true;
  });
}

function sortItems(items: Property[], mode: SortMode): Property[] {
  const copy = [...items];
  switch (mode) {
    case 'price-asc':
      return copy.sort((a, b) => a.priceFrom - b.priceFrom);
    case 'yield-desc':
      return copy.sort((a, b) => b.yieldPct - a.yieldPct);
    case 'score-desc':
      return copy.sort((a, b) => b.score - a.score);
    case 'recent':
    default:
      return copy; // в БД будет сортировка по `favorites.created_at desc`
  }
}

/* ────────────────────────────────────────────────────────────── */
/*  Grouped view — секции по локациям                              */
/* ────────────────────────────────────────────────────────────── */

interface LocationGroup {
  key: string;
  /** Заголовок секции, e.g. «🇷🇺 Россия · Крым» */
  title: string;
  /** Подзаголовок, e.g. «3 объекта · 48 млн ₽» */
  subtitle: string;
  items: Property[];
}

function buildGroups(items: Property[], sel: LocationSelection): LocationGroup[] {
  // Уровень группировки — самый узкий из выбранных
  const level: 'country' | 'region' | 'city' | 'district' = sel.citySlug
    ? 'district'
    : sel.regionSlug
      ? 'city'
      : sel.countrySlug
        ? 'region'
        : 'country';

  const map = new Map<string, LocationGroup>();
  for (const p of items) {
    const loc = p.locationDetails;
    if (!loc) continue;

    let key: string;
    let title: string;

    if (level === 'country') {
      key = loc.countrySlug;
      title = `${loc.countryFlag} ${loc.country}`;
    } else if (level === 'region') {
      key = `${loc.countrySlug}/${loc.regionSlug ?? ''}`;
      title = `${loc.countryFlag} ${loc.region ?? loc.country}`;
    } else if (level === 'city') {
      key = `${loc.countrySlug}/${loc.regionSlug ?? ''}/${loc.citySlug}`;
      title = `${loc.countryFlag} ${loc.city}`;
    } else {
      key = `${loc.citySlug}/${loc.districtSlug ?? ''}`;
      title = `${loc.countryFlag} ${loc.district ?? loc.city}`;
    }

    const existing = map.get(key);
    if (existing) existing.items.push(p);
    else map.set(key, { key, title, subtitle: '', items: [p] });
  }

  // Заполняем subtitle (count + сумма)
  for (const group of map.values()) {
    const totalRub = group.items.reduce((s, p) => s + p.priceFrom, 0);
    const sumLabel =
      totalRub >= 1_000_000_000
        ? `${(totalRub / 1_000_000_000).toFixed(2).replace('.', ',')} млрд ₽`
        : `${Math.round(totalRub / 1_000_000)} млн ₽`;
    group.subtitle = `${group.items.length} ${pluralObj(group.items.length)} · ${sumLabel}`;
  }

  return Array.from(map.values()).sort((a, b) => b.items.length - a.items.length);
}

function pluralObj(n: number): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return 'объектов';
  if (last > 1 && last < 5) return 'объекта';
  if (last === 1) return 'объект';
  return 'объектов';
}

/* ────────────────────────────────────────────────────────────── */
/*  Screen                                                         */
/* ────────────────────────────────────────────────────────────── */

function FavoritesScreen() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<FavoritesTab>('items');
  const [selection, setSelection] = useState<LocationSelection>({});
  const [sort, setSort] = useState<SortMode>('recent');
  const [group, setGroup] = useState<GroupMode>('flat');
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null);

  const allItems = FAVORITED_PROPERTIES;

  // Если выбрана пользовательская коллекция — фильтруем по её propertyIds
  const collectionFilteredItems = useMemo(() => {
    if (!activeCollectionId) return allItems;
    const col = USER_COLLECTIONS.find((c) => c.id === activeCollectionId);
    if (!col) return allItems;
    return allItems.filter((p) => col.propertyIds.includes(p.id));
  }, [allItems, activeCollectionId]);

  // Затем — location filter
  const filteredItems = useMemo(
    () => applyLocationFilter(collectionFilteredItems, selection),
    [collectionFilteredItems, selection],
  );

  const sortedItems = useMemo(() => sortItems(filteredItems, sort), [filteredItems, sort]);
  const groups = useMemo(
    () => (group === 'by-location' ? buildGroups(sortedItems, selection) : []),
    [group, sortedItems, selection],
  );

  const counts: Record<FavoritesTab, number> = {
    items: allItems.length,
    collections: USER_COLLECTIONS.length,
    alerts: 0,
    viewed: 0,
  };

  const activeCollection = activeCollectionId
    ? USER_COLLECTIONS.find((c) => c.id === activeCollectionId)
    : null;

  const handleOpenUnit = (id: string) => {
    // Пока используем legacy /property/:id; SEO-slug URL подключим, когда
    // у saved юнитов будет полный slug в БД (см. §2.4 ТЗ)
    navigate({ to: `/property/${id}` });
  };

  return (
    <div className="px-4">
      <FavoritesHeader items={filteredItems} totalCount={allItems.length} />

      <FavoritesTabs active={tab} onChange={setTab} counts={counts} />

      {tab === 'items' && (
        <>
          {/* Если выбрана коллекция — показываем её chip с кнопкой «×» */}
          {activeCollection && (
            <div className="flex items-center gap-2 pb-1 pt-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-brand-blue)] px-3 py-1.5 text-[13px] font-semibold text-white">
                <CollectionGlyph icon={activeCollection.icon} size={14} />
                {activeCollection.name}
                <button
                  type="button"
                  aria-label="Убрать фильтр коллекции"
                  onClick={() => setActiveCollectionId(null)}
                  className="ml-0.5 grid h-4 w-4 place-items-center rounded-full bg-white/25 transition-colors hover:bg-white/35"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M6 6l12 12M18 6L6 18"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </span>
            </div>
          )}

          {/* Контролы — только если есть что фильтровать/сортировать */}
          {allItems.length > 0 && (
            <>
              <LocationFilter
                items={collectionFilteredItems}
                selection={selection}
                onChange={setSelection}
              />

              <ViewControls
                sort={sort}
                group={group}
                onSortChange={setSort}
                onGroupChange={setGroup}
              />
            </>
          )}

          {sortedItems.length === 0 ? (
            <EmptyState />
          ) : group === 'flat' ? (
            <FlatList items={sortedItems} onOpen={handleOpenUnit} />
          ) : (
            <GroupedList groups={groups} onOpen={handleOpenUnit} />
          )}
        </>
      )}

      {tab === 'collections' && (
        <CollectionsTab
          collections={USER_COLLECTIONS}
          allItems={allItems}
          onSelect={(id) => {
            setActiveCollectionId(id);
            setSelection({});
            setTab('items');
          }}
          onCreate={() => {
            // TODO: open modal для создания коллекции (sync with API)
          }}
        />
      )}

      {tab === 'alerts' && (
        <div className="pt-2">
          <TabStub
            title="Алерты"
            subtitle="Saved searches с триггерами цены / yield / появления новых юнитов"
            description="Здесь будут saved searches и их notifications: «новые студии в Адлере до 9 млн», «yield ↑ 12% в Алуште», «появился свободный юнит в Якиманке»."
            docRef="favorites.tsx §Alerts"
          />
        </div>
      )}

      {tab === 'viewed' && (
        <div className="pt-2">
          <TabStub
            title="Просмотрено"
            subtitle="История юнитов, которые ты смотрел"
            description="Последние 200 юнитов, которые ты открывал. Сортировка по дате. Можно сразу добавить в избранное или сравнить."
            docRef="favorites.tsx §Viewed"
          />
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/*  FlatList                                                       */
/* ────────────────────────────────────────────────────────────── */

function FlatList({
  items,
  onOpen,
}: {
  items: Property[];
  onOpen: (id: string) => void;
}) {
  return (
    <div className="space-y-4 pb-6 pt-3">
      {items.map((p) => (
        <UnitCard key={p.id} property={p} onOpen={() => onOpen(p.id)} />
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/*  GroupedList — sticky-секции по локациям                        */
/* ────────────────────────────────────────────────────────────── */

function GroupedList({
  groups,
  onOpen,
}: {
  groups: LocationGroup[];
  onOpen: (id: string) => void;
}) {
  return (
    <div className="pb-6">
      {groups.map((g) => (
        <section key={g.key} className="mb-6">
          {/* Sticky header секции */}
          <div className="sticky top-[44px] z-10 -mx-4 mb-3 bg-bg/95 px-4 py-2 backdrop-blur-md">
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="text-[17px] font-bold tracking-tight text-label">
                {g.title}
              </h2>
              <span className="shrink-0 text-[12px] font-medium text-label-2">
                {g.subtitle}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {g.items.map((p) => (
              <UnitCard key={p.id} property={p} onOpen={() => onOpen(p.id)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/*  Empty state                                                    */
/* ────────────────────────────────────────────────────────────── */

function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-16 text-center">
      <div
        className="grid h-16 w-16 place-items-center rounded-full"
        style={{ background: 'var(--color-glass-thin)' }}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M12 21s-7-4.5-9-9c-1.3-2.9 0-6 3-6 2 0 3 1 4 2.5l2-2.5c2-2 6-1 6 3.5 0 4.5-6 11.5-6 11.5z"
            stroke="var(--color-label-2)"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h3 className="text-[16px] font-semibold text-label">
        Здесь будут ваши избранные
      </h3>
      <p className="max-w-[280px] text-[13px] text-label-2">
        Найдите юнит в поиске или ленте и нажмите{' '}
        <span aria-hidden>♡</span> — он появится здесь и в коллекциях.
      </p>
    </div>
  );
}
